import type { ToolId, ToolOptions } from '../types'

interface ProcessResult {
  blob: Blob
  name: string
  width: number
  height: number
}

const supportedOutputTypes = new Set(['image/jpeg', 'image/png', 'image/webp'])

function createCanvas(width: number, height: number) {
  const canvas = document.createElement('canvas')
  canvas.width = Math.max(1, Math.round(width))
  canvas.height = Math.max(1, Math.round(height))
  const context = canvas.getContext('2d', { willReadFrequently: true })
  if (!context) throw new Error('이 브라우저에서는 이미지 캔버스를 사용할 수 없습니다.')
  return { canvas, context }
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const image = new Image()
    image.onload = () => {
      URL.revokeObjectURL(url)
      resolve(image)
    }
    image.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error(`${file.name} 파일을 읽을 수 없습니다.`))
    }
    image.src = url
  })
}

function toBlob(canvas: HTMLCanvasElement, type: string, quality: number) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('결과 이미지를 만들지 못했습니다.'))),
      type,
      quality,
    )
  })
}

function baseName(filename: string) {
  return filename.replace(/\.[^/.]+$/, '').replace(/[^a-zA-Z0-9가-힣_-]+/g, '-') || 'image'
}

function extensionForMime(mime: string) {
  if (mime === 'image/jpeg') return 'jpg'
  if (mime === 'image/webp') return 'webp'
  return 'png'
}

function suffixForTool(toolId: ToolId) {
  const suffixes: Record<ToolId, string> = {
    compress: 'compressed',
    resize: 'resized',
    crop: 'cropped',
    'convert-jpg': 'converted',
    'convert-png': 'converted',
    'html-to-image': 'captured',
    rotate: 'rotated',
    watermark: 'watermarked',
    editor: 'edited',
    upscale: 'upscaled',
    'remove-bg': 'transparent',
    meme: 'meme',
    blur: 'blurred',
  }
  return suffixes[toolId]
}

function ensureSafeCanvas(width: number, height: number) {
  const pixels = width * height
  if (width > 16384 || height > 16384 || pixels > 80_000_000) {
    throw new Error('결과 이미지가 브라우저 처리 한도를 넘습니다. 더 작은 크기를 선택해 주세요.')
  }
}

function drawCoverText(
  context: CanvasRenderingContext2D,
  text: string,
  y: number,
  maxWidth: number,
  fontSize: number,
) {
  const normalized = text.trim()
  if (!normalized) return
  let size = fontSize
  context.font = `800 ${size}px "Nanum Gothic", sans-serif`
  while (context.measureText(normalized).width > maxWidth && size > 18) {
    size -= 2
    context.font = `800 ${size}px "Nanum Gothic", sans-serif`
  }
  context.textAlign = 'center'
  context.textBaseline = 'middle'
  context.lineJoin = 'round'
  context.strokeStyle = '#000000'
  context.lineWidth = Math.max(4, size * 0.12)
  context.strokeText(normalized, maxWidth / 2 + (context.canvas.width - maxWidth) / 2, y, maxWidth)
  context.fillStyle = '#ffffff'
  context.fillText(normalized, maxWidth / 2 + (context.canvas.width - maxWidth) / 2, y, maxWidth)
}

function applyBackgroundRemoval(context: CanvasRenderingContext2D, width: number, height: number, tolerance: number) {
  const imageData = context.getImageData(0, 0, width, height)
  const data = imageData.data
  const cornerIndexes = [0, (width - 1) * 4, (height - 1) * width * 4, (height * width - 1) * 4]
  const background = cornerIndexes.reduce(
    (color, index) => ({ r: color.r + data[index], g: color.g + data[index + 1], b: color.b + data[index + 2] }),
    { r: 0, g: 0, b: 0 },
  )
  background.r /= 4
  background.g /= 4
  background.b /= 4
  const threshold = Math.max(4, tolerance) * 4.4
  const feather = Math.max(12, threshold * 0.35)

  for (let index = 0; index < data.length; index += 4) {
    const distance = Math.sqrt(
      (data[index] - background.r) ** 2 +
        (data[index + 1] - background.g) ** 2 +
        (data[index + 2] - background.b) ** 2,
    )
    if (distance <= threshold) {
      data[index + 3] = 0
    } else if (distance < threshold + feather) {
      data[index + 3] = Math.round(data[index + 3] * ((distance - threshold) / feather))
    }
  }
  context.putImageData(imageData, 0, 0)
}

function flattenTransparency(canvas: HTMLCanvasElement) {
  const flattened = createCanvas(canvas.width, canvas.height)
  flattened.context.fillStyle = '#ffffff'
  flattened.context.fillRect(0, 0, canvas.width, canvas.height)
  flattened.context.drawImage(canvas, 0, 0)
  return flattened.canvas
}

export async function readImageSize(file: File) {
  const image = await loadImage(file)
  return { width: image.naturalWidth, height: image.naturalHeight }
}

export async function processImage(file: File, toolId: ToolId, options: ToolOptions): Promise<ProcessResult> {
  const image = await loadImage(file)
  let width = image.naturalWidth
  let height = image.naturalHeight
  if (!width || !height) throw new Error(`${file.name}의 이미지 크기를 확인할 수 없습니다.`)

  let source = createCanvas(width, height)
  source.context.drawImage(image, 0, 0)

  if (toolId === 'resize') {
    let targetWidth: number
    let targetHeight: number
    if (options.resizeMode === 'percent') {
      targetWidth = width * (options.percent / 100)
      targetHeight = height * (options.percent / 100)
    } else if (options.keepAspect) {
      const widthRatio = options.width > 0 ? options.width / width : Number.POSITIVE_INFINITY
      const heightRatio = options.height > 0 ? options.height / height : Number.POSITIVE_INFINITY
      let ratio = Math.min(widthRatio, heightRatio)
      if (!Number.isFinite(ratio)) ratio = 1
      if (options.noUpscale) ratio = Math.min(1, ratio)
      targetWidth = width * ratio
      targetHeight = height * ratio
    } else {
      targetWidth = options.width
      targetHeight = options.height
    }
    if (options.noUpscale) {
      targetWidth = Math.min(targetWidth, width)
      targetHeight = Math.min(targetHeight, height)
    }
    ensureSafeCanvas(targetWidth, targetHeight)
    const resized = createCanvas(targetWidth, targetHeight)
    resized.context.imageSmoothingEnabled = true
    resized.context.imageSmoothingQuality = 'high'
    resized.context.drawImage(source.canvas, 0, 0, resized.canvas.width, resized.canvas.height)
    source = resized
    width = resized.canvas.width
    height = resized.canvas.height
  }

  if (toolId === 'crop') {
    const x = Math.max(0, Math.min(width - 1, Math.round(options.cropX)))
    const y = Math.max(0, Math.min(height - 1, Math.round(options.cropY)))
    const cropWidth = Math.max(1, Math.min(width - x, Math.round(options.cropWidth || width)))
    const cropHeight = Math.max(1, Math.min(height - y, Math.round(options.cropHeight || height)))
    const cropped = createCanvas(cropWidth, cropHeight)
    cropped.context.drawImage(source.canvas, x, y, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight)
    source = cropped
    width = cropWidth
    height = cropHeight
  }

  if (toolId === 'rotate') {
    const angle = options.rotation
    const swapsSides = Math.abs(angle) === 90
    const rotated = createCanvas(swapsSides ? height : width, swapsSides ? width : height)
    rotated.context.translate(rotated.canvas.width / 2, rotated.canvas.height / 2)
    rotated.context.rotate((angle * Math.PI) / 180)
    rotated.context.scale(options.flipHorizontal ? -1 : 1, options.flipVertical ? -1 : 1)
    rotated.context.drawImage(source.canvas, -width / 2, -height / 2)
    source = rotated
    width = rotated.canvas.width
    height = rotated.canvas.height
  }

  if (toolId === 'upscale') {
    const targetWidth = width * options.upscale
    const targetHeight = height * options.upscale
    ensureSafeCanvas(targetWidth, targetHeight)
    const upscaled = createCanvas(targetWidth, targetHeight)
    upscaled.context.imageSmoothingEnabled = true
    upscaled.context.imageSmoothingQuality = 'high'
    upscaled.context.drawImage(source.canvas, 0, 0, targetWidth, targetHeight)
    source = upscaled
    width = targetWidth
    height = targetHeight
  }

  if (toolId === 'editor') {
    const edited = createCanvas(width, height)
    edited.context.filter = `brightness(${options.brightness}%) contrast(${options.contrast}%) saturate(${options.saturation}%) grayscale(${options.grayscale}%)`
    edited.context.drawImage(source.canvas, 0, 0)
    edited.context.filter = 'none'
    source = edited
  }

  if (toolId === 'blur') {
    const blurred = createCanvas(width, height)
    const padding = options.blur * 2
    blurred.context.filter = `blur(${options.blur}px)`
    blurred.context.drawImage(source.canvas, -padding, -padding, width + padding * 2, height + padding * 2)
    blurred.context.filter = 'none'
    source = blurred
  }

  if (toolId === 'watermark') {
    const context = source.context
    const size = Math.max(12, Math.min(options.fontSize, width / 3))
    const padding = Math.max(16, size * 0.6)
    context.save()
    context.globalAlpha = options.opacity / 100
    context.fillStyle = options.textColor
    context.font = `800 ${size}px "Nanum Gothic", sans-serif`
    context.textBaseline = 'middle'
    const textWidth = context.measureText(options.watermarkText).width
    const positions = {
      'top-left': [padding, padding + size / 2],
      'top-right': [width - textWidth - padding, padding + size / 2],
      center: [(width - textWidth) / 2, height / 2],
      'bottom-left': [padding, height - padding - size / 2],
      'bottom-right': [width - textWidth - padding, height - padding - size / 2],
    }
    const [x, y] = positions[options.watermarkPosition]
    context.shadowColor = 'rgba(0,0,0,.35)'
    context.shadowBlur = Math.max(2, size * 0.08)
    context.fillText(options.watermarkText || 'zozinimg', x, y)
    context.restore()
  }

  if (toolId === 'meme') {
    const context = source.context
    const bandHeight = Math.max(72, Math.round(height * 0.16))
    const fontSize = Math.max(26, Math.min(72, width * 0.075))
    context.save()
    context.fillStyle = 'rgba(0, 0, 0, .58)'
    if (options.memeTop.trim()) context.fillRect(0, 0, width, bandHeight)
    if (options.memeBottom.trim()) context.fillRect(0, height - bandHeight, width, bandHeight)
    drawCoverText(context, options.memeTop, bandHeight / 2, width * 0.9, fontSize)
    drawCoverText(context, options.memeBottom, height - bandHeight / 2, width * 0.9, fontSize)
    context.restore()
  }

  if (toolId === 'remove-bg') applyBackgroundRemoval(source.context, width, height, options.tolerance)

  let outputType = supportedOutputTypes.has(file.type) ? file.type : 'image/png'
  if (toolId === 'convert-jpg') outputType = 'image/jpeg'
  if (toolId === 'convert-png') outputType = options.outputFormat
  if (toolId === 'remove-bg') outputType = 'image/png'
  if (outputType === 'image/gif') outputType = 'image/png'

  const outputCanvas = outputType === 'image/jpeg' ? flattenTransparency(source.canvas) : source.canvas
  const quality = Math.max(0.2, Math.min(1, options.quality / 100))
  let blob = await toBlob(outputCanvas, outputType, quality)

  if (toolId === 'compress' && outputType === file.type && blob.size >= file.size) {
    blob = file
  }

  return {
    blob,
    width,
    height,
    name: `${baseName(file.name)}-${suffixForTool(toolId)}.${extensionForMime(blob.type || outputType)}`,
  }
}
